<template>
	<div class="table-wrapper">
		<perfect-scrollbar>
			<div class="table table--expandable-rows cols--5">
				<div
					v-for="item in data"
					:key="item.id"
					class="table__row"
					:class="{ 'table__row--expanded': item.isExpanded }"
				>
					<div class="row__visible">
						<div class="row-actions">
							<div
								class="row-actions__button no-rotate"
								@click="setFavoriteForUser(item)"
							>
								<svgicon
									class="icon--large"
									:class="{ 'icon--outline': !item.isFavorite }"
									name="star-full"
								></svgicon>
							</div>
						</div>

						<div class="table__cell capitalize">
							<div class="cell__header">
								<span class="header__title">{{
									$t('dashboardStudentName')
								}}</span>
							</div>
							{{ item.name }}
						</div>
						<div class="table__cell">
							<div class="cell__header">
								<span class="header__title">{{
									$t('dashboardStudentEmail')
								}}</span>
							</div>
							{{ item.email }}
						</div>
						<div class="table__cell">
							<div class="add-student-wrapper">
								<span class="header__title">
									<ja-add-button @click.native="openModalAddCareer(item)">
										{{ $t('addNew') + ' ' + $t('career') }}
									</ja-add-button>
								</span>
							</div>
						</div>
						<div class="row-actions">
							<div
								class="row-actions__button"
								:class="{ 'row-actions__button--disabled': !item.isExpandable }"
								@click="toggleExapndableItem(item)"
							>
								<svgicon name="arrow-right"></svgicon>
							</div>
						</div>
					</div>

					<div
						class="row__expandable"
						:class="{ 'no-actions-left': item.isExpandable }"
					>
						<ja-loader
							v-if="!item.areSimulationsFetched && item.isExpanded"
						></ja-loader>

						<div v-if="item.areSimulationsFetched && item.isExpanded">
							<div class="row__headers">
								<div class="table__header">{{ $t('simulationLbl') }}</div>
								<div class="table__header">{{ $t('dashboardStudentJob') }}</div>
								<div class="table__header">
									{{ $t('dashboardStudentLifeStage') }}
								</div>
								<div class="table__header">
									<span class="header__title">{{
										$t('dashboardStudentCompleted')
									}}</span>
									<span class="header__subtitle">{{
										$t('dashboardExpenseCategory')
									}}</span>
								</div>
								<div class="table__header col-size--md">
									<span class="header__title">{{
										$t('dashboardAttempt')
									}}</span>
									<span class="header__subtitle">{{
										$t('dashboardCurrent')
									}}</span>
								</div>
							</div>
							<div
								v-for="(simulation, i) in item.simulations"
								:key="i"
								class="row__content"
							>
								<div class="table__cell">
									{{ simulation.sim.sessionName }}
								</div>
								<div class="table__cell">
									{{ simulation.cmsCareerJobTitle | forHumans('jobTitle') }}
								</div>
								<div class="table__cell">
									{{ simulation.cmsLifeStage | forHumans('name') }}
								</div>
								<div class="table__cell">
									<button
										class="btn btn-link btn--bold btn--lg"
										@click.prevent="openCategoriesForSim(simulation)"
									>
										{{ simulation.completedCategories }} of
										{{ simulation.availableCategories }}
									</button>
								</div>
								<div class="table__cell col-size--md">
									{{ simulation.simulationTry | nonZeroValue }}
								</div>
								<div class="row-actions">
									<div class="row-actions__button no-rotate">
										<ja-force-complete-life-stage
											:student="simulation"
											@openConfirmationDialog="forceLifeStageCompletion"
										></ja-force-complete-life-stage>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</perfect-scrollbar>
	</div>
</template>

<script>
import StudentsService from '@/services/StudentsService';
import ForceCompleteLifeStage from '@/components/AdminSection/elements/ForceCompleteLifeStage';
import UserData from '@/mixins/UserData';
import Loader from './Loader';
import AddButton from '@/components/AdminSection/elements/AddButton';
import _ from 'lodash';

export default {
	components: {
		jaForceCompleteLifeStage: ForceCompleteLifeStage,
		jaLoader: Loader,
		jaAddButton: AddButton
	},
	filters: {
		forHumans(value, arg) {
			return value ? value[arg] : 'N/A';
		},
		nonZeroValue(value) {
			return value || 1;
		}
	},
	mixins: [UserData],
	props: {
		items: {
			type: Array,
			default: () => []
		},
		facility: {
			type: Number,
			default: 0
		}
	},
	data() {
		return {
			data: [],
			showOpenModalAddCareer: false,
			itemStudentSelected: null
		};
	},
	watch: {
		items() {
			this.dataAdapter();
		}
	},
	mounted() {
		this.dataAdapter();
	},
	methods: {
		dataAdapter() {
			this.data = this.items.map(item => {
				return {
					id: item.id,
					career: item.career,
					name: `${item.firstName} ${item.lastName}`,
					email: item.emailAddress,
					isExpanded: false,
					areSimulationsFetched: false,
					isExpandable: item.simulations.some(sim => !!sim.simulationId),
					isFavorite: item.simulations.some(s => s.isFavouriteForCurrentUser),
					simulations: this.adaptSimulations(item.simulations),
					favoriteStudentsList: item.simulations.flatMap(sim =>
						sim.favoriteIds.flatMap(fav => fav.id)
					)
				};
			});
		},
		adaptSimulations(simulations) {
			// reorder the final list by sim's name in desc order
			return _.orderBy(
				// make simulations unique by simId
				// That way we will show only one record
				_.uniqBy(
					// filter simulations to get only those that are part of the current facility
					// this is made here instead of the query because of favorites.
					// the icon is not marked on different facility since the toggle gets only studentIds
					// from the currently selected facility
					simulations.filter(s => s.sim && s.sim.facility_ID === this.facility),
					'simulationId'
				),
				'sim.sessionName',
				'desc'
			);
		},
		toggleExapndableItem(item) {
			// Do nothing if item is not expandable
			if (!item.isExpandable) {
				return;
			}
			item.isExpanded = !item.isExpanded;

			// if data is already fetched, do not submit request again
			// we don't need the same data over and over.
			if (item.areSimulationsFetched) {
				return;
			}

			StudentsService.getSimulationsByUser(item.id, this.facility).then(
				({ data }) => {
					item.simulations = this.adaptSimulations(data.simulations);
					item.areSimulationsFetched = true;
				}
			);
		},
		openCategoriesForSim(sim) {
			this.$emit('openCompletedCategories', sim);
		},
		forceLifeStageCompletion(sim) {
			this.$emit('forceLifeStage', sim);
		},
		setFavoriteForUser(item) {
			let list = item.simulations.map(student => {
				return {
					studentId: student.id,
					userId: this.userData.userId
				};
			});

			if (item.isFavorite) {
				list = item.favoriteStudentsList;
			}

			StudentsService.toggleFavoriteStudentsForUser(list, item.isFavorite).then(
				() => {
					this.$emit('refetchData');
				}
			);
		},
		openModalAddCareer(item) {
			this.$emit('modalOpened', true);
			this.$emit('itemSelected', item);
		}
	}
};
</script>

<style>
.add-student-wrapper {
	padding-left: 100px;
}
</style>
